/*
  A stored phone number as "(+33)-612345678" -- the dialling code set apart
  from the rest, so a reader can see at a glance which country it is.

  Numbers are stored the way react-phone-input-2 hands them over: all digits,
  country code included, no "+" and no separator. "33612345678" is a French
  number but reads as one long run of digits, with nothing to say where the
  country code stops.

  ---- why not libphonenumber-js, which is already a dependency ----

  Because of where this is used. The app has no route-level code splitting, so
  anything imported from an admin screen lands in the single main bundle that
  every visitor downloads. libphonenumber IS already bundled -- but inside the
  beneficiary portal's own chunk, and a static import from here pulled a second
  copy into main: measured at +30.26 kB gzipped on a 1.26 MB bundle, paid by
  everyone, for a field only an administrator sees.

  Splitting a number does not need the library. It needs the set of dialling
  codes, which is 206 entries and fits in under a kilobyte. The table below was
  generated from libphonenumber-js's own metadata.min.json -- the same source,
  so it cannot drift from the library's view of the world -- rather than typed
  out by hand.

  What is given up by not parsing properly: no validity check, no national
  grouping ("6 12 34 56 78"), no country letters. None of that is wanted here;
  the point is one clearly separated code and then the number as entered.
*/

/*
  Every dialling code in use, shortest first. Regenerate with:

    node -e "const m=require('libphonenumber-js/metadata.min.json');
             console.log(Object.keys(m.country_calling_codes).sort(
               (a,b)=>a.length-b.length||a.localeCompare(b)).join(' '))"
*/
const CALLING_CODES = new Set(
  (
    "1 7 20 27 30 31 32 33 34 36 39 40 41 43 44 45 46 47 48 49 51 52 53 54 " +
    "55 56 57 58 60 61 62 63 64 65 66 81 82 84 86 90 91 92 93 94 95 98 211 " +
    "212 213 216 218 220 221 222 223 224 225 226 227 228 229 230 231 232 233 " +
    "234 235 236 237 238 239 240 241 242 243 244 245 246 247 248 249 250 251 " +
    "252 253 254 255 256 257 258 260 261 262 263 264 265 266 267 268 269 290 " +
    "291 297 298 299 350 351 352 353 354 355 356 357 358 359 370 371 372 373 " +
    "374 375 376 377 378 380 381 382 383 385 386 387 389 420 421 423 500 501 " +
    "502 503 504 505 506 507 508 509 590 591 592 593 594 595 596 597 598 599 " +
    "670 672 673 674 675 676 677 678 679 680 681 682 683 685 686 687 688 689 " +
    "690 691 692 850 852 853 855 856 880 886 960 961 962 963 964 965 966 967 " +
    "968 970 971 972 973 974 975 976 977 992 993 994 995 996 998 "
  )
    .trim()
    .split(" ")
);

/*
  Longest match first: 212 is Morocco, but 21 and 2 are not codes at all, and
  1 is. Codes are one to three digits and no code is a prefix of another, so
  trying three then two then one finds the only possible split.
*/
const splitCallingCode = (digits) => {
  for (const length of [3, 2, 1]) {
    const head = digits.slice(0, length);
    if (head.length === length && CALLING_CODES.has(head)) {
      return { code: head, rest: digits.slice(length) };
    }
  }
  return null;
};

export const formatPhone = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const digits = raw.replace(/[^0-9]/g, "");
  const split = digits ? splitCallingCode(digits) : null;

  /*
    Anything unrecognised is shown exactly as stored.

    An admin screen is where someone goes to find out what is really in the
    record, so a number that is too short, mistyped, or carries a code the
    table does not know must stay readable. Prettifying it away -- or showing a
    dash -- would hide the very thing being looked for.
  */
  if (!split || !split.rest) return raw;

  return `(+${split.code})-${split.rest}`;
};
